import { memo } from 'react';
import type { GroundPoint, Projection, Size } from './geometry';
import { gridLines } from './geometry';

/** Residual from a transformed Record position to its observed Field point. */
export interface ResidualSegment {
  id: number;
  from: GroundPoint;
  to: GroundPoint;
}

/** 95% uncertainty of a transformed Record position (ground radius). */
export interface UncertaintyCircle {
  id: number;
  center: GroundPoint;
  radius: number;
}

/** A comparison fit's transformed Record position and where it is now. */
export interface FitMovement {
  id: number;
  point: string;
  previous: GroundPoint;
  current: GroundPoint | null;
}

/** Ground-aligned grid that moves with the data. */
export const GridLayer = memo(function GridLayer({
  projection,
  size,
}: {
  projection: Projection;
  size: Size;
}) {
  const { xs, ys } = gridLines(projection, size);
  return (
    <g
      aria-hidden="true"
      className="pointer-events-none stroke-border/75"
      strokeWidth={1}
      shapeRendering="crispEdges"
    >
      {xs.map((x) => (
        <line
          key={`x${x}`}
          x1={Math.round(x) + 0.5}
          x2={Math.round(x) + 0.5}
          y1={0}
          y2={size.height}
        />
      ))}
      {ys.map((y) => (
        <line
          key={`y${y}`}
          y1={Math.round(y) + 0.5}
          y2={Math.round(y) + 0.5}
          x1={0}
          x2={size.width}
        />
      ))}
    </g>
  );
});

/**
 * Uncertainty circles about transformed Record positions, drawn at the
 * residual vectors' scale (`radiusScale` pixels per ground unit).
 */
export const UncertaintyLayer = ({
  circles,
  projection,
  radiusScale,
}: {
  circles: readonly UncertaintyCircle[];
  projection: Projection;
  radiusScale: number;
}) => (
  <g aria-hidden="true" className="pointer-events-none">
    {circles.map((circle) => {
      const r = circle.radius * radiusScale;
      if (!(r >= 1)) return null;
      const { x, y } = projection.toXY(circle.center.n, circle.center.e);
      return (
        <circle
          key={circle.id}
          data-uncertainty-id={circle.id}
          cx={x}
          cy={y}
          r={r}
          className="fill-record/5 stroke-record/60"
          strokeWidth={1}
          strokeDasharray="3 3"
        />
      );
    })}
  </g>
);

/**
 * Previous (or accepted) fit positions and their movement to the displayed
 * fit, both at true scale. Field observations do not move.
 */
export const ComparisonLayer = ({
  movements,
  projection,
  referenceLabel,
}: {
  movements: readonly FitMovement[];
  projection: Projection;
  referenceLabel: string;
}) => (
  <g className="pointer-events-none">
    {movements.map((movement) => {
      const start = projection.toXY(movement.previous.n, movement.previous.e);
      const end = movement.current
        ? projection.toXY(movement.current.n, movement.current.e)
        : null;
      return (
        <g
          key={movement.id}
          data-previous-point={movement.point}
          aria-hidden="true"
        >
          <title>{`${referenceLabel} transformed Record ${movement.point}`}</title>
          {end && (
            <line
              data-fit-movement={movement.point}
              x1={start.x}
              y1={start.y}
              x2={end.x}
              y2={end.y}
              className="stroke-record"
              strokeWidth={1.5}
              strokeLinecap="round"
            />
          )}
          <circle
            cx={start.x}
            cy={start.y}
            r={4.5}
            className="fill-surface-sunken stroke-fg-muted"
            strokeWidth={1.25}
            strokeDasharray="2 1.75"
          />
        </g>
      );
    })}
  </g>
);

/**
 * Residual vectors stretched by `stretch` from the transformed end, so a
 * sub-pixel misfit stays visible; 1 draws them at true scale.
 */
export const ResidualLayer = ({
  segments,
  projection,
  stretch,
  markerId,
}: {
  segments: readonly ResidualSegment[];
  projection: Projection;
  stretch: number;
  markerId: string;
}) => (
  <g aria-hidden="true" className="pointer-events-none">
    <defs>
      <marker
        id={markerId}
        viewBox="0 0 10 10"
        refX={8}
        refY={5}
        markerWidth={5}
        markerHeight={5}
        orient="auto-start-reverse"
      >
        <path d="M0 0.5 9.5 5 0 9.5Z" className="fill-residual" />
      </marker>
    </defs>
    {segments.map((segment) => {
      const from = projection.toXY(segment.from.n, segment.from.e);
      const to = projection.toXY(segment.to.n, segment.to.e);
      const x2 = from.x + (to.x - from.x) * stretch;
      const y2 = from.y + (to.y - from.y) * stretch;
      const long = Math.hypot(x2 - from.x, y2 - from.y) >= 8;
      return (
        <g key={segment.id} data-residual-id={segment.id}>
          <line
            x1={from.x}
            y1={from.y}
            x2={x2}
            y2={y2}
            className="stroke-residual"
            strokeWidth={1.5}
            strokeLinecap="round"
            markerEnd={long ? `url(#${markerId})` : undefined}
          />
        </g>
      );
    })}
  </g>
);
